import { Box, styled, Table } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import useBoolean from 'hooks/useBoolean';
import { useCallback, useMemo, useState } from 'react';
import { useExpanded, useRowSelect, useSortBy, useTable } from 'react-table';
import ListSubmissionUserColumnShape from './listSubmissionUserColumnShape';
import downloadFile from 'utils/downloadFile';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    '& td:first-of-type': {
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
    },
    '& td:last-of-type': {
        textAlign: 'center',
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const HeadTableCell = styled(TableCell)(({ theme }) => ({
    padding: 0,
    fontSize: 16,
    fontWeight: 600,
    borderBottom: 0,
    color: theme.palette.text.primary,
    '&:first-of-type': {
        paddingLeft: 16,
    },
    '&:last-child': {
        textAlign: 'center',
    },
}));

const BodyTableCell = styled(HeadTableCell)(({ theme }) => ({
    padding: '10px 10px',
    color: theme.palette.text.primary,
}));

const ListSubmissionUserTable = ({listSubmissionUserList }) => {
    const tableData = useMemo(() => listSubmissionUserList, [listSubmissionUserList]);
    const [downloadId, setDownloadId] = useState();

    const downloading = useBoolean();
    
    const handleDownloadCertificate = useCallback(async (row) => {
        try {
            downloading.onTrue();
            setDownloadId(row.id);

            const downloadFileUrl = `${process.env.REACT_APP_API_BASE_URL}/api/prv/submissions/certificate`;
            const params = {
                certificate: row.certificate,
            };

            await downloadFile(downloadFileUrl, params);
        } finally {
            setDownloadId(null);
            downloading.onFalse();
        }
    }, []);

    const columns = useMemo(
        () => ListSubmissionUserColumnShape({ 
            handleDownloadCertificate, 
            downloading: downloading,
            downloadingId: downloadId,
        }), [handleDownloadCertificate, downloading.value, downloadId]); 
    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
        useTable(
            {
                columns,
                data: tableData,
            },
            useSortBy,
            useExpanded,
            useRowSelect,
        );

    return (
        <Box>
            <Table
                {...getTableProps()}
                sx={{
                    borderSpacing: '0 1rem',
                    borderCollapse: 'separate',
                }}
            >
                <TableHead>
                    {headerGroups.map((headerGroup, key) => (
                        <TableRow
                            key={key}
                            {...headerGroup.getHeaderGroupProps()}
                        >
                            {headerGroup.headers.map((column, index) => (
                                <HeadTableCell
                                    key={index}
                                    {...column.getHeaderProps(
                                        column.getSortByToggleProps(),
                                    )}
                                >
                                    {column.render('Header')}
                                </HeadTableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>

                <TableBody {...getTableBodyProps()}>
                    {rows.map((row, index) => {
                        prepareRow(row);

                        return (
                            <StyledTableRow
                                key={index}
                                {...row.getRowProps()}
                            >
                                {row.cells.map((cell, index) => {
                                    return (
                                        <BodyTableCell
                                            key={index}
                                            {...cell.getCellProps()}
                                        >
                                            {cell.render('Cell')}
                                        </BodyTableCell>
                                    );
                                })}
                            </StyledTableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
};

export default ListSubmissionUserTable;
